/**
 * Contract for the commit-attribution repo registry endpoints. Imported by:
 *   - excellent/app/api/commit-attr/repos/**           (server)
 *   - packages/ui/src/blocks/repo-list.tsx + settings  (client)
 *
 * Shape mirrors excellent_repos in migrations v23.
 */

import { z } from "zod";
import type { Operation } from "../types";
import { idNumericParams } from "./_common";

export const repoStatus = z.enum(["pending", "active", "archived"]);
export type RepoStatus = z.infer<typeof repoStatus>;

export const repoRowSchema = z.object({
  id: z.number(),
  remote_url: z.string().nullable().optional(),
  display_name: z.string(),
  status: repoStatus,
  registered_at: z.string(),
  last_synced_at: z.string().nullable().optional(),
});
export type RepoRow = z.infer<typeof repoRowSchema>;

export const reposListQuery = z.object({
  status: repoStatus.optional(),
});

export const reposList: Operation<
  void,
  z.infer<typeof reposListQuery>,
  void,
  { repos: RepoRow[] }
> = {
  method: "GET",
  path: () => "/api/commit-attr/repos",
  query: reposListQuery,
  response: z.object({ repos: z.array(repoRowSchema) }),
};

export const repoConfirm: Operation<
  { id: number },
  void,
  void,
  { id: number; status: RepoStatus }
> = {
  method: "POST",
  path: ({ id }) => `/api/commit-attr/repos/${id}/confirm`,
  params: idNumericParams,
  response: z.object({ id: z.number(), status: repoStatus }),
  // status pending→active (and status filter variants).
  invalidates: [reposList],
};

export const repoArchive: Operation<
  { id: number },
  void,
  void,
  { id: number; status: RepoStatus }
> = {
  method: "POST",
  path: ({ id }) => `/api/commit-attr/repos/${id}/archive`,
  params: idNumericParams,
  response: z.object({ id: z.number(), status: repoStatus }),
  // status → archived (and status filter variants).
  invalidates: [reposList],
};

export const repos = {
  list: reposList,
  confirm: repoConfirm,
  archive: repoArchive,
};
